import { env } from "@repo/design-system/setup/env";
import type { SearchResponseHit } from "typesense/lib/Typesense/Documents";
import type {
  FilterInfo,
  FilterResultPage,
  FilterSearchParams,
} from "../../types/filter";
import type { GeoBoundaries } from "../../types/geo";
import type { JobExcerpt, JobMapPin, SimilarJob } from "../../types/job";
import type {
  GeoApifyPayload,
  GeoApifyResponse,
} from "../../utils/geoapify/geoapify";

export type Item = SearchResponseHit<JobExcerpt> & Record<string, string>;

const TRAILING_SLASH_RE = /\/+$/;

interface FetchContext {
  baseUrl: string;
  /**
   * Headers to forward from the incoming request when doing a server-side
   * self-loopback fetch. Empty on the client (relative fetches inherit
   * cookies automatically).
   */
  forwardedHeaders: Record<string, string>;
}

/**
 * Computes the base URL and any headers that must be forwarded for a
 * server-side loopback fetch.
 *
 * Two problems this solves together:
 *
 * 1. Multi-hostname deployments — the same Vercel project can be served
 *    under several hostnames (e.g. `jobs.tkmsgroup.com` and
 *    `skills.fsxjobboard.de` both point at the tkms app). A static
 *    build-time `NEXT_PUBLIC_BASE_URL` would force every SSR fetch
 *    cross-host to a single canonical origin, breaking multi-tenant
 *    routing and host-based feature gates (like the skill matcher guard).
 *    We derive the origin from the incoming request's `x-forwarded-host`
 *    header so each hostname loops back to itself.
 *
 * 2. Vercel Deployment Protection — on preview deployments (and any other
 *    cookie-gated environment), the browser request arrives with a session
 *    cookie, but a fresh `fetch()` inside a Server Component is a separate
 *    HTTP request that does not inherit that cookie and gets rejected with
 *    401. We forward the incoming `cookie` header so the loopback fetch
 *    authenticates as the same caller.
 */
async function getFetchContext(): Promise<FetchContext> {
  // Client-side: relative URLs, cookies auto-included by the browser. Under a
  // reverse-proxy basePath the API lives at `/jobs/api/*`, but `fetch("/api")`
  // resolves against the origin root and drops the prefix — so seed baseUrl
  // with NEXT_PUBLIC_BASE_PATH. Empty / unset for apps mounted at root.
  if (typeof window !== "undefined") {
    return { baseUrl: env.NEXT_PUBLIC_BASE_PATH ?? "", forwardedHeaders: {} };
  }

  try {
    const { headers } = await import("next/headers");
    const h = await headers();
    const host = h.get("x-forwarded-host") ?? h.get("host");
    if (host) {
      const proto = h.get("x-forwarded-proto") ?? "https";
      // Append NEXT_PUBLIC_BASE_PATH (set per-app to match next.config's
      // basePath) so loopback URLs include the path prefix the customer's
      // reverse proxy preserves. Empty / unset for apps mounted at root.
      const basePath = env.NEXT_PUBLIC_BASE_PATH ?? "";
      const forwardedHeaders: Record<string, string> = {};
      const cookie = h.get("cookie");
      if (cookie) {
        forwardedHeaders.cookie = cookie;
      }
      return {
        baseUrl: `${proto}://${host}${basePath}`,
        forwardedHeaders,
      };
    }
  } catch {
    // `headers()` throws outside a request scope (e.g. during build-time
    // static generation or scripts). Fall through to the env-based default.
  }

  // Fallback: build-time static generation, scripts, or other non-request
  // contexts where no incoming host is available. Strip any trailing slash
  // on the env value so concatenating with a leading-slash path does not
  // produce a double-slash URL like `https://host.com//api/...`.
  if (env.ENVIRONMENT !== "development") {
    return {
      baseUrl: env.NEXT_PUBLIC_BASE_URL.replace(TRAILING_SLASH_RE, ""),
      forwardedHeaders: {},
    };
  }

  // Development fallback
  return {
    baseUrl: `http://localhost:${env.NEXT_PUBLIC_PORT ?? 3000}`,
    forwardedHeaders: {},
  };
}

// Filter API
export const filterApi = {
  /**
   * Fetches filter information based on locale and optional subclient
   */
  async fetchInfo(locale: string, subclient?: string): Promise<FilterInfo> {
    const params = new URLSearchParams();
    params.append("locale", locale);
    if (subclient) {
      params.append("subclient", subclient);
    }

    const { baseUrl, forwardedHeaders } = await getFetchContext();
    const res = await fetch(`${baseUrl}/api/filter/info?${params.toString()}`, {
      headers: forwardedHeaders,
    });
    if (!res.ok) {
      throw new Error(`Failed to fetch filter info: ${res.status}`);
    }
    return res.json();
  },

  /**
   * Performs a paginated job search
   */
  async fetchJobList(
    locale: string,
    filterSearchParams: FilterSearchParams,
    page: number
  ): Promise<FilterResultPage> {
    const { baseUrl, forwardedHeaders } = await getFetchContext();
    const res = await fetch(`${baseUrl}/api/filter/query`, {
      method: "POST",
      headers: {
        ...forwardedHeaders,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ...filterSearchParams,
        locale,
        page,
      }),
    });

    if (!res.ok) {
      throw new Error(`Failed to fetch job list: ${res.status}`);
    }

    return res.json();
  },

  /**
   * Fetches map pins for all jobs matching the filter
   */
  async fetchMapPins(
    locale: string,
    filterSearchParams: FilterSearchParams
  ): Promise<{ jobMapPins: JobMapPin[] }> {
    const { baseUrl, forwardedHeaders } = await getFetchContext();
    const res = await fetch(`${baseUrl}/api/filter/map`, {
      method: "POST",
      headers: {
        ...forwardedHeaders,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        locale,
        filterSearchParams,
      }),
    });

    if (!res.ok) {
      throw new Error(`Failed to fetch map pins: ${res.status}`);
    }

    return res.json();
  },

  /**
   * Performs autocomplete search
   */
  async fetchAutocomplete<T extends Item[] | string[]>(
    query: string,
    locale: string
  ): Promise<T> {
    const { baseUrl, forwardedHeaders } = await getFetchContext();
    const response = await fetch(
      `${baseUrl}/api/filter/autocomplete?query=${encodeURIComponent(query)}&locale=${locale}`,
      { headers: forwardedHeaders }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch autocomplete: ${response.status}`);
    }

    return response.json();
  },
};

// Similar Jobs API
export const similarJobsApi = {
  /**
   * Fetches the similar jobs shown at the bottom of a job ad. A plain GET so
   * the response is cacheable by the CDN — the filter map is serialised with
   * sorted keys to keep that cache key stable across renders.
   */
  async fetchSimilarJobs({
    id,
    locale,
    client,
    filters,
  }: {
    id: string;
    locale: string;
    client?: string;
    filters: Record<string, string>;
  }): Promise<SimilarJob[]> {
    const params = new URLSearchParams({ id, locale });
    if (client) {
      params.append("client", client);
    }
    params.append(
      "filters",
      JSON.stringify(Object.fromEntries(Object.entries(filters).sort()))
    );

    const { baseUrl, forwardedHeaders } = await getFetchContext();
    const res = await fetch(
      `${baseUrl}/api/similar-jobs?${params.toString()}`,
      {
        headers: forwardedHeaders,
      }
    );

    if (!res.ok) {
      throw new Error(`Failed to fetch similar jobs: ${res.status}`);
    }

    return res.json();
  },
};

// Hybrid Search API (Skill Matcher + Typesense Filters)
export const hybridApi = {
  async fetchQuestionResults(
    locale: string,
    searchQuestion: string,
    rerankInstruction: string,
    filterSearchParams: Omit<
      FilterSearchParams,
      "postingDateFilter" | "subclient"
    >,
    page: number,
    // 2026-09-15: react-query's per-fetch signal, so a superseded request
    // (new key, invalidation) closes its connection instead of racing the
    // replacement for the cache slot. NOTE: the server does not observe the
    // abort — the route runs retrieval + rerank to completion regardless.
    // What actually bounds duplicate server work is the debounced refetch
    // (coverage-expander) and the route's in-flight coalescing.
    signal?: AbortSignal
  ): Promise<FilterResultPage> {
    const { baseUrl, forwardedHeaders } = await getFetchContext();
    const res = await fetch(`${baseUrl}/api/filter/skill-matcher-hybrid`, {
      method: "POST",
      headers: {
        ...forwardedHeaders,
        "Content-Type": "application/json",
      },
      signal,
      body: JSON.stringify({
        searchQuestion,
        rerankInstruction,
        locale,
        page,
        filter: filterSearchParams.filter,
        geoLocation: filterSearchParams.geoLocation,
        boundaries: filterSearchParams.boundaries,
        radius: filterSearchParams.radius,
        searchQuery: filterSearchParams.searchQuery,
      }),
    });

    if (res.status === 429) {
      const err = new Error("Too many requests. Please try again later.");
      (err as Error & { status: number }).status = 429;
      throw err;
    }
    if (!res.ok) {
      // The status rides on every failure so the retry policy can tell a
      // client error (never retry) from a server one (retry once).
      const err = new Error(
        `Failed to fetch hybrid search results: ${res.status}`
      );
      (err as Error & { status: number }).status = res.status;
      throw err;
    }

    return res.json();
  },
};

// Geo API
export const geoApi = {
  /**
   * Fetches geolocation data from Geoapify
   */
  async fetchGeoLocation<TPayload extends GeoApifyPayload>(
    payload: TPayload
  ): Promise<GeoApifyResponse<TPayload>> {
    const { baseUrl, forwardedHeaders } = await getFetchContext();
    const res = await fetch(`${baseUrl}/api/filter/geo`, {
      method: "POST",
      headers: {
        ...forwardedHeaders,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      throw new Error(`Failed to fetch geo location: ${res.status}`);
    }

    return res.json();
  },

  /**
   * Fetches boundaries for a place
   */
  async fetchBoundaries(
    placeId: string,
    locale?: string
  ): Promise<GeoBoundaries> {
    const { baseUrl, forwardedHeaders } = await getFetchContext();
    const res = await fetch(`${baseUrl}/api/filter/geo`, {
      method: "POST",
      headers: {
        ...forwardedHeaders,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ type: "place", placeId, lang: locale }),
    });

    if (!res.ok) {
      throw new Error(`Failed to fetch boundaries: ${res.status}`);
    }

    const data = await res.json();
    // Ensure the response matches GeoBoundaries interface
    return {
      coordinates: data.coordinates,
      placeId,
    };
  },
};
